import { useNavigate } from "react-router-dom";

import { Button } from "components/ui/Button";
import { Notice, NoticeText } from "components/ui/Notice";
import { pathGenerators } from "utils/routes";

/*
 * The classifications that evidence public entitlement on their own: a GMS card, or a temporary
 * one. Dalen's rule — "for patient to be public, we must have their GMS and validate that it is
 * in the date. Anything else means private patient."
 *
 * Note what this does NOT say. A patient outside this list is not thereby ineligible: entitlement
 * also follows from age or condition — over-60s for pneumococcal, for instance — and those rules
 * are too involved for the record to settle. So the absence of a GMS card means only that we
 * cannot confirm entitlement here, which is why this warns rather than blocks.
 */
const PUBLICLY_ENTITLED_CLASSIFICATIONS = ["02", "04"];

/**
 * "We cannot tell whether the HSE will pay for this one."
 *
 * The screen used to carry the opposite claim — that a patient with no classification could have
 * nothing sent to NIIS at all. That was wrong twice over: the vaccine message never reads the
 * field (PV1.20 is hardcoded to PUBLIC), and a GMS card is evidence of entitlement rather than a
 * condition of it, so treating its absence as disqualifying inverts what the card means.
 *
 * Deliberately not a blocker, and deliberately on this form rather than the immunisations screen:
 * it bears on one submission, in HealthLink mode, and a notice that greets a GP on a screen they
 * opened to read history is one they learn to dismiss.
 *
 * ---
 *
 * Two things here look like oversights and are not. Both were decided on 2026-10-06, after the
 * second of them was reported as a bug.
 *
 * **Save stays enabled beside it.** The wording is Dalen's, and it presumes a submission is
 * possible: "please ensure HSE entitlement *before submitting* to Healthlink. Ineligible vaccines
 * are to be submitted as manual." The GP checks, then sends — or switches to Manual if the patient
 * turns out to be ineligible. Disabling Save would make that instruction impossible to follow, and
 * would stop a privately-classified patient who *is* entitled — someone over 60 getting
 * pneumococcal, say — from ever going through HealthLink. That is the exact block Dalen reported
 * as a bug in the first place, so withholding Save here would reinstate it under another name.
 *
 * **It keeps the red blocking tone**, although it blocks nothing. Muting it to `info` was tried
 * and rejected: blue reads as "nothing is being asked of you", when something is. An amber middle
 * tone was added and then reverted — the red was wanted. So a red notice sitting beside a working
 * Save button is the agreed state, not a styling bug.
 *
 * `NoPpsnClaimNotice`, on this same form, is in the same position: red, and no blocker either.
 */
export const HseEntitlementNotice = ({ patient, patientId }) => {
  const navigate = useNavigate();

  if (!patient) return null;

  const classification = patient.financialClass?.trim();
  const isPubliclyEntitled =
    !!classification && PUBLICLY_ENTITLED_CLASSIFICATIONS.includes(classification);

  if (isPubliclyEntitled) return null;

  const editablePatientId = patientId ?? patient.id;

  return (
    <Notice
      testId="hse-entitlement-notice"
      action={
        !!editablePatientId && (
          <Button
            variant="outline"
            size="small"
            onClick={() =>
              navigate(pathGenerators.patient.details(editablePatientId))
            }
          >
            Edit patient details
          </Button>
        )
      }
    >
      <NoticeText>
        Patient type not set or Private; please ensure HSE entitlement before
        submitting to Healthlink. Ineligible vaccines are to be submitted as
        manual.
      </NoticeText>
    </Notice>
  );
};
